import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue';

export interface UseViewportIntersectOptions extends IntersectionObserverInit {
  onBeforeEnter?: (target: HTMLElement) => void;
  onIsEntering?: (target: HTMLElement) => void;
  onHasEntered?: (target: HTMLElement) => void;
  onBeforeLeave?: (target: HTMLElement) => void;
  onIsLeaving?: (target: HTMLElement) => void;
  onHasLeft?: (target: HTMLElement) => void;
}

const DEFAULT_OBSERVER_OPTIONS = {
  root: null,
  threshold: [0, 0.001, 0.5, 0.009, 1],
};

export function useViewportIntersect(options: UseViewportIntersectOptions) {
  const { onBeforeEnter, onIsEntering, onHasEntered, onBeforeLeave, onIsLeaving, onHasLeft, ...observerOptions } =
    options;

  const target: Ref<HTMLElement | null> = ref(null);
  let observer: IntersectionObserver | null = null;
  let lastRatio = 0;
  let enteredState = false;
  let enteringState = false;
  let beforeEnterState = false;

  const stop = () => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  };

  onMounted(() => {
    const el = target.value!;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const r = entry.intersectionRatio;
          const isCurrentlyEntering = r >= lastRatio;
          lastRatio = r;

          if (!isCurrentlyEntering) {
            if (r === 0 && beforeEnterState) {
              beforeEnterState = false;
              enteringState = false;
              enteredState = false;
              onHasLeft?.(el);
            } else if (r <= 0.5 && enteringState) {
              enteringState = false;
              enteredState = false;
              onIsLeaving?.(el);
            } else if (r <= 1 && enteredState) {
              enteredState = false;
              onBeforeLeave?.(el);
            }
          } else {
            if (r === 1 && !enteredState) {
              enteredState = true;
              enteringState = true;
              beforeEnterState = true;
              onHasEntered?.(el);
            } else if (r >= 0.5 && !enteringState) {
              enteringState = true;
              beforeEnterState = true;
              onIsEntering?.(el);
            } else if (r > 0 && !beforeEnterState) {
              beforeEnterState = true;
              onBeforeEnter?.(el);
            }
          }
        }
      },
      { ...DEFAULT_OBSERVER_OPTIONS, ...observerOptions },
    );

    observer.observe(el);
  });

  onBeforeUnmount(() => {
    stop();
  });

  return { target, stop };
}
